import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, Truck } from "lucide-react";
import { login, formatApiError } from "@/lib/auth";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] grid place-items-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <span className="grid place-items-center h-10 w-10 rounded-lg bg-[#ff6b35] text-white">
            <Truck size={22} strokeWidth={2.5} />
          </span>
          <span className="font-display font-extrabold text-xl text-white">Come Pick Up My Trash</span>
        </div>
        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="bg-white rounded-2xl p-8 border-[3px] border-[#ff6b35] shadow-2xl"
        >
          <div className="text-[#ff6b35] font-bold text-xs uppercase tracking-widest">Admin</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Sign in to the dashboard</h1>
          <p className="text-[#333] text-sm mt-2">Manage bookings, payments, and crews.</p>

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="font-bold text-sm">Email</Label>
              <Input
                data-testid="login-email"
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@comepickupmytrash.com"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-bold text-sm">Password</Label>
              <Input
                data-testid="login-password"
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5"
              />
            </div>
          </div>

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={submitting}
            className="w-full mt-6 bg-[#ff6b35] hover:bg-[#ff8c42] text-white font-bold rounded-lg h-11"
          >
            {submitting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Signing in…</> : <><Lock size={16} className="mr-2" /> Sign in</>}
          </Button>

          <p className="text-xs text-[#333] mt-4 text-center">
            Authorized personnel only. All actions are logged.
          </p>
        </form>
        <p className="text-center text-white/50 text-xs mt-6">
          <a href="/" className="hover:text-white">← Back to customer site</a>
        </p>
      </div>
    </div>
  );
}
